import React from 'react';
import { PlusOutlined } from '@ant-design/icons';

import { getDefaultBrandKit } from 'common/brand-kit/projection';
import type { StoredBrandKit } from 'common/brand-kit/types';

import { Select, type SelectOption } from 'admin/components/common/Form';
import type { SelectProps } from 'admin/components/common/Form/Select';

import css from './BrandKitSelect.scss';

const NONE_KEY = '__none';
const CREATE_KEY = '__create';

type Props = {
	kits: StoredBrandKit[];
	value?: string | null;
	onChange: (brandKitId: string) => void;
	onCreate?: () => void;
	includeNoBrandKit?: boolean;
	theme?: 'light' | 'dark';
	className?: string;
	disabled?: boolean;
	id?: string;
	ariaLabel?: string;
	menuPlacement?: SelectProps['menuPlacement'];
};

type BrandKitOption = SelectOption & {
	label: string;
	richLabel: React.ReactNode;
};

function KitPalette({ kit }: { kit: StoredBrandKit }) {
	const colors = kit.design.tokens.collections
		.flatMap(collection => collection.tokens)
		.filter(token => token.type === 'color')
		.slice(0, 4);

	return (
		<span className={css.palette} aria-hidden="true">
			{Array.from({ length: 4 }, (_, index) => {
				const color = colors[index];
				return color ? (
					<i key={color.id} style={{ backgroundColor: String(color.value) }} />
				) : (
					<i key={index} />
				);
			})}
		</span>
	);
}

function KitOption({ kit, isDefault }: { kit: StoredBrandKit; isDefault: boolean }) {
	return (
		<span className={css.option}>
			<KitPalette kit={kit} />
			<span className={css.optionName}>{kit.name}</span>
			{isDefault ? <span className={css.defaultBadge}>Default</span> : <span aria-hidden="true" />}
		</span>
	);
}

export default function BrandKitSelect({
	kits,
	value,
	onChange,
	onCreate,
	includeNoBrandKit = false,
	theme = 'light',
	className,
	disabled = false,
	id,
	ariaLabel = 'Choose Brand Kit',
	menuPlacement,
}: Props) {
	const selectedKit = kits.find(kit => kit.id === value);
	const defaultKit = getDefaultBrandKit(kits);
	const selectedLabel = selectedKit?.name ?? 'No Brand Kit';
	const accessibleLabel = `${ariaLabel}: ${selectedLabel}${
		selectedKit && defaultKit?.id === selectedKit.id ? ', Default' : ''
	}`;
	const options: BrandKitOption[] = [
		...(includeNoBrandKit
			? [
					{
						value: NONE_KEY,
						label: 'No Brand Kit',
						richLabel: (
							<span className={css.option}>
								<span className={css.palettePlaceholder} aria-hidden="true" />
								<span className={css.optionName}>No Brand Kit</span>
								<span aria-hidden="true" />
							</span>
						),
					},
				]
			: []),
		...kits.map(kit => {
			const isDefault = defaultKit?.id === kit.id;
			return {
				value: kit.id,
				label: `${kit.name}${isDefault ? ' Default' : ''}`,
				richLabel: <KitOption kit={kit} isDefault={isDefault} />,
			};
		}),
		...(onCreate
			? [
					{
						value: CREATE_KEY,
						label: 'Create kit',
						richLabel: (
							<span className={css.createOption}>
								<PlusOutlined /> Create kit
							</span>
						),
					},
				]
			: []),
	];

	return (
		<div
			className={`${css.root} ${css[theme]}${className ? ` ${className}` : ''}`}
			onMouseDown={event => event.stopPropagation()}
			onClick={event => event.stopPropagation()}
		>
			<Select<BrandKitOption>
				theme={theme}
				label={{ htmlFor: id }}
				aria-label={accessibleLabel}
				menuPlacement={menuPlacement}
				options={options}
				formatOptionLabel={option => option.richLabel}
				value={selectedKit?.id ?? (includeNoBrandKit ? NONE_KEY : undefined)}
				disabled={disabled}
				eventListeners={{
					onChange: nextValue => {
						if (nextValue === CREATE_KEY) onCreate?.();
						else onChange(nextValue === NONE_KEY ? '' : String(nextValue));
					},
				}}
			/>
		</div>
	);
}
